import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  FiscalProfileEditor,
  type FiscalProfileEditorAudience,
  type FiscalProfileEditorRef,
} from "@/components/features/fiscal-profile/FiscalProfileEditor";
import { FiscalProfileAutoFillBanner } from "@/components/features/fiscal-profile/FiscalProfileAutoFillBanner";
import {
  fiscalProfileEditorValuesFromSources,
  toCreateFiscalProfilePayload,
  toExoneratedRegimePayload,
  toUpdateFiscalProfilePayload,
} from "@/lib/fiscalProfileEditorMappers";
import { tenantKeys } from "@/lib/queryKeys";
import { INDUSTRY_FISCAL_DEFAULTS } from "@/lib/industries";
import {
  getFiscalProfile,
  createFiscalProfile,
  updateFiscalProfile,
  type FiscalCompanyType,
  type FiscalTaxType,
  type IraeFrequency,
} from "@/services/fiscalService";
import { updateTenantExoneratedRegime } from "@/services/authService";
import type { TenantCurrentResponse } from "@/services/authService";

export interface FiscalProfileStepRef {
  save: () => Promise<void>;
  isValid: () => boolean;
}

/**
 * Take the YYYY-MM-DD date portion from a date-only or ISO string. Never
 * `new Date()`: the API serializes `date` columns as UTC-anchored ISO
 * timestamps, and reparsing one shifts a day back in behind-UTC browsers.
 */
function normalizeActivityStartDate(value: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  return match ? match[1] : "";
}

const FISCAL_COMPANY_TYPES = ["unipersonal", "sas", "srl", "sa"] as const;

function isFiscalCompanyType(
  v: string | null | undefined
): v is FiscalCompanyType {
  return v != null && FISCAL_COMPANY_TYPES.includes(v as FiscalCompanyType);
}

interface FiscalProfileStepProps {
  industry?: string | null;
  /** When set, prefills activity start if empty (from company / DGI). */
  activityStartDateFromCompany?: string | null;
  companyTypeFromCompany?: FiscalCompanyType | null;
  onValidationChange?: (valid: boolean) => void;
  /** Current tenant (exoneration fields). Pass from onboarding parent. */
  tenant?: TenantCurrentResponse | null;
  /** Who's filling in the form. Defaults to 'owner' (self-onboarding); pass
   *  'accountant' from the accountant-onboarding flow so the section copy
   *  addresses the contador in third person ("la empresa del cliente"
   *  instead of "tu empresa"). */
  audience?: FiscalProfileEditorAudience;
}

export const FiscalProfileStep = forwardRef<
  FiscalProfileStepRef,
  FiscalProfileStepProps
>(function FiscalProfileStep(
  {
    industry,
    activityStartDateFromCompany,
    companyTypeFromCompany,
    onValidationChange,
    tenant,
    audience = "owner",
  },
  ref
) {
  const queryClient = useQueryClient();
  const { data: profile } = useQuery({
    queryKey: ["fiscal-profile"] as const,
    queryFn: getFiscalProfile,
    retry: false,
  });

  const editorRef = useRef<FiscalProfileEditorRef>(null);
  const [saving, setSaving] = useState(false);

  const baseFromApi = useMemo(
    () => fiscalProfileEditorValuesFromSources(profile ?? null, tenant ?? null),
    [profile, tenant]
  );

  const [industryOverrides, setIndustryOverrides] = useState<{
    companyType?: FiscalCompanyType;
    isExportingServices?: boolean;
    hasEmployees?: boolean;
    taxes?: FiscalTaxType[];
    iraeFrequency?: IraeFrequency | "";
  } | null>(null);

  useEffect(() => {
    if (profile) {
      setIndustryOverrides(null);
      return;
    }
    const defaults = industry ? INDUSTRY_FISCAL_DEFAULTS[industry] : undefined;
    if (!defaults) {
      setIndustryOverrides(null);
      return;
    }
    setIndustryOverrides({
      companyType:
        defaults.companyType != null && !isFiscalCompanyType(companyTypeFromCompany)
          ? defaults.companyType
          : undefined,
      isExportingServices: defaults.isExporter ?? undefined,
      hasEmployees: defaults.hasEmployees ?? undefined,
      taxes: defaults.taxes != null ? defaults.taxes.slice() : undefined,
      iraeFrequency: defaults.iraeFrequency ?? undefined,
    });
  }, [profile, industry, companyTypeFromCompany]);

  const initialValues = useMemo(() => {
    let v = { ...baseFromApi };
    if (!profile && isFiscalCompanyType(companyTypeFromCompany)) {
      v.companyType = companyTypeFromCompany;
    }
    if (industryOverrides) {
      if (industryOverrides.companyType != null) v.companyType = industryOverrides.companyType;
      if (industryOverrides.isExportingServices != null)
        v.isExportingServices = industryOverrides.isExportingServices;
      if (industryOverrides.hasEmployees != null)
        v.hasEmployees = industryOverrides.hasEmployees;
      if (industryOverrides.taxes != null) v.taxes = industryOverrides.taxes;
      if (industryOverrides.iraeFrequency != null)
        v.iraeFrequency = industryOverrides.iraeFrequency;
    }
    if (activityStartDateFromCompany?.trim() && !profile) {
      const normalized = normalizeActivityStartDate(activityStartDateFromCompany);
      if (normalized && !v.activityStartDate.trim()) {
        v = { ...v, activityStartDate: normalized };
      }
    }
    return v;
  }, [
    baseFromApi,
    profile,
    companyTypeFromCompany,
    industryOverrides,
    activityStartDateFromCompany,
  ]);

  const resetToken = useMemo(
    () =>
      `${profile?.id ?? "np"}-${profile?.updatedAt ?? ""}-${tenant?.exoneratedRegimeLegend ?? ""}-${String(tenant?.exoneratedRegimeEnabled)}`,
    [profile?.id, profile?.updatedAt, tenant?.exoneratedRegimeLegend, tenant?.exoneratedRegimeEnabled]
  );

  const isValid = useCallback((): boolean => {
    return editorRef.current?.validate() ?? false;
  }, []);

  const save = useCallback(async (): Promise<void> => {
    const editor = editorRef.current;
    if (!editor?.validate()) return;
    const s = editor.getValues();

    setSaving(true);
    try {
      const industryForPayload = tenant?.industry ?? industry ?? null;
      const exPayload = toExoneratedRegimePayload(s, industryForPayload);

      if (profile) {
        await updateFiscalProfile(toUpdateFiscalProfilePayload(s, {}));
      } else {
        await createFiscalProfile(toCreateFiscalProfilePayload(s, {}));
      }
      await updateTenantExoneratedRegime(exPayload);
      await queryClient.invalidateQueries({ queryKey: ["fiscal-profile"] });
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
    } finally {
      setSaving(false);
    }
  }, [profile, queryClient, tenant?.industry, industry]);

  useImperativeHandle(
    ref,
    () => ({
      save,
      isValid,
    }),
    [save, isValid]
  );

  return (
    <div className="fx-onb-fiscal-form space-y-3">
      <FiscalProfileAutoFillBanner profile={profile} />
      <FiscalProfileEditor
        ref={editorRef}
        idPrefix="onboarding-fiscal"
        initialValues={initialValues}
        resetToken={resetToken}
        hideCompanyType={isFiscalCompanyType(companyTypeFromCompany)}
        hideAgendaFields={false}
        industryCode={tenant?.industry ?? industry ?? null}
        industryForLegends={tenant?.industry ?? industry ?? null}
        showSubmitButton={false}
        isSaving={saving}
        onValidationChange={onValidationChange}
        audience={audience}
      />
    </div>
  );
});
